/**
 * Stacked sub-cells for the LAE Productivity Schools table.
 *
 * Ports LAE `features/productivity/StackedCell.jsx` (schools-by-assignment spec §3; EMERGE build
 * spec §5.4). A row is one school × funnel. Every stacked column renders one fixed-height line per
 * assignment line, then a bold subtotal line, so line i sits at the same height in every column and
 * an assignment reads straight across the table. The school column is not stacked.
 *
 *   render: (r) => <StackedCell row={r} k="calls" fmt={(v) => <LineNum value={v as number} />} />
 *
 * One line: the line and its subtotal would repeat each other, so one bold line draws the SUBTOTAL
 * values with the line's identity laid over them (`singleValue`). No lines (consolidated, or an
 * older response without a breakdown): only the bold subtotal line, with no top rule.
 *
 * Deliberate differences from LAE:
 * - No Tooltip content. EMERGE hover labels are names only, so the facts LAE put in a tooltip
 *   (assignment type, cycle, status, range; the specialist list) ride as a native `title` on the
 *   truncated text: a data readout, not helper text.
 * - Specialists show NAMES inline (owner, 2026-10-29) instead of initial chips. A specialist who
 *   worked carries a small success dot before the name (shape, not colour alone). Not interactive.
 * - No zebra tint export (dropped, §5.2), no rubric band styling (§0.3).
 * - `linesOf` / `singleValue` / `specialistTotals` live in `lae-school-rows` (P6) and the
 *   formatters in `lae-format` (P2); this file keeps no second copy of either rule.
 * - Raw palette and colour-mix classes become tokens: hairline rules, subtle for zero values.
 */
import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import type { LaeProdAssignmentLine, LaeProdSpecialist } from '@/lib/types';
import { fmtDay, fmtDayLong, fmtNum, fmtPct, fmtShortDuration, fmtTalkTotal, titleCase } from './lae-format';
import { linesOf, singleValue, specialistTotals } from './lae-school-rows';
import type { LaeSchoolRow, LaeSingleValue } from './lae-school-rows';

const DASH = '—';

/** One line: fixed height so lines align across columns, pulled out over the cell padding so rules run edge to edge. */
const LINE = '-mx-3 h-6 truncate whitespace-nowrap px-3 leading-6';
/** Rule between assignment lines. */
const HAIRLINE = 'border-t border-hairline';
/** Subtotal line under two or more lines. */
const SUBTOTAL = 'border-t border-hairline-strong font-semibold text-fg';

type StackedRow = LaeSchoolRow;
type StackedLine = LaeProdAssignmentLine;
type SingleLineValue = LaeSingleValue<LaeSchoolRow>;

export interface StackedFmtContext {
  line?: StackedLine;
  /** True for the subtotal line, and for the one bold line of a single-assignment row. */
  total: boolean;
  single?: boolean;
  row: StackedRow;
}

export interface StackedCellProps<V> {
  /** Table row: subtotal fields flattened on, plus its assignment lines. */
  row: StackedRow;
  /** Value for one line. On a single-line row it receives the `singleValue` (subtotal + identity). */
  get?: (line: StackedLine | SingleLineValue, index: number) => V;
  /** Formats a value; defaults to the value itself, `—` when null or undefined. */
  fmt?: (value: V | undefined, ctx: StackedFmtContext) => ReactNode;
  /** Metric key read off each line and off the row (the subtotal) when `get` / `total` are absent. */
  k?: string;
  /** Subtotal value, formatted with `fmt`. */
  total?: (row: StackedRow) => V;
  /** Replaces `get` + `fmt` for each line. */
  renderLine?: (line: StackedLine, index: number) => ReactNode;
  /** Replaces `total` + `fmt` for the subtotal line. */
  renderTotal?: (row: StackedRow) => ReactNode;
  className?: string;
}

function readKey(source: unknown, key: string | undefined): unknown {
  if (!key || source === null || typeof source !== 'object') return undefined;
  return (source as Record<string, unknown>)[key];
}

function defaultFormat(value: unknown): ReactNode {
  if (value === null || value === undefined || value === '') return DASH;
  if (typeof value === 'string' || typeof value === 'number') return value;
  return DASH;
}

function lineKey(line: StackedLine, index: number): string {
  const id: unknown = line.assignment_id;
  return typeof id === 'string' || typeof id === 'number' ? `${id}` : `line-${index}`;
}

export function StackedCell<V = unknown>({ row, get, fmt, k, total, renderLine, renderTotal, className }: StackedCellProps<V>) {
  const lines = linesOf(row);
  const format = (value: V | undefined, ctx: StackedFmtContext): ReactNode =>
    fmt ? fmt(value, ctx) : defaultFormat(value);

  const first = lines[0];
  if (lines.length === 1 && first !== undefined) {
    const v = singleValue(row, first);
    const value = get ? get(v, 0) : (readKey(v, k) as V | undefined);
    return (
      <div className={cx('flex min-w-0 flex-col', className)}>
        <div className={cx(LINE, 'font-semibold text-fg')}>
          {renderLine ? renderLine(first, 0) : format(value, { line: first, total: true, single: true, row })}
        </div>
      </div>
    );
  }

  let subtotal: ReactNode = null;
  if (renderTotal) subtotal = renderTotal(row);
  else if (total) subtotal = format(total(row), { total: true, row });
  else if (k) subtotal = format(readKey(row, k) as V | undefined, { total: true, row });

  return (
    <div className={cx('flex min-w-0 flex-col', className)}>
      {lines.map((line: StackedLine, i: number) => (
        <div key={lineKey(line, i)} className={cx(LINE, i > 0 && HAIRLINE)}>
          {renderLine
            ? renderLine(line, i)
            : format(get ? get(line, i) : (readKey(line, k) as V | undefined), { line, total: false, row })}
        </div>
      ))}
      <div className={cx(LINE, lines.length > 0 ? SUBTOTAL : 'font-semibold text-fg')}>{subtotal}</div>
    </div>
  );
}

// ---- value renderers (zeros and blanks dimmed, tabular figures) ---------------------------------

type NumValue = number | null | undefined;

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isZeroish = (v: unknown): boolean => !isNum(v) || v === 0;

export function LineNum({ value }: { value: NumValue }) {
  return <span className={cx('tabular-nums', isZeroish(value) && 'text-fg-subtle')}>{fmtNum(value)}</span>;
}

export function LinePct({ value }: { value: NumValue }) {
  return <span className={cx('tabular-nums', isZeroish(value) && 'text-fg-subtle')}>{fmtPct(value)}</span>;
}

/** Seconds. `avg` uses the short form; otherwise the total form. */
export function LineDuration({ value, avg = false }: { value: NumValue; avg?: boolean }) {
  return (
    <span className={cx('tabular-nums', isZeroish(value) && 'text-fg-subtle')}>
      {avg ? fmtShortDuration(value) : fmtTalkTotal(value)}
    </span>
  );
}

/** `YYYY-MM-DD` → the short day, the long day as the title. Null → `—`, dimmed. */
export function LineDate({ value }: { value: string | null | undefined }) {
  const text = value ? fmtDay(value) : DASH;
  if (!value || text === DASH) return <span className="tabular-nums text-fg-subtle">{DASH}</span>;
  return (
    <span className="tabular-nums" title={fmtDayLong(value)}>
      {text}
    </span>
  );
}

/** Leads worked over records, e.g. `12 / 40`. */
export function WorkedOf({ worked, records }: { worked: NumValue; records: NumValue }) {
  return (
    <span className="tabular-nums">
      <span className={isZeroish(worked) ? 'text-fg-subtle' : 'text-fg'}>{fmtNum(worked)}</span>
      <span className="text-fg-subtle"> / </span>
      <span className={isZeroish(records) ? 'text-fg-subtle' : 'text-fg-muted'}>{fmtNum(records)}</span>
    </span>
  );
}

// ---- assignment name ----------------------------------------------------------------------------

/** The identity fields an assignment line (or a `singleValue`) carries. */
export interface AssignmentIdentity {
  name?: string | null;
  assignment_type?: string | null;
  cycle?: string | null;
  status?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

function rangeText(line: AssignmentIdentity): string | null {
  const from = line.start_date ? fmtDayLong(line.start_date) : null;
  const to = line.end_date ? fmtDayLong(line.end_date) : null;
  if (from && to) return `${from} to ${to}`;
  if (from) return `From ${from}`;
  if (to) return `Until ${to}`;
  return null;
}

/** Assignment name for one line; type, cycle, status and range as the title. Inactive lines are muted. */
export function AssignmentName({ line }: { line: AssignmentIdentity | null | undefined }) {
  const name = line?.name || line?.assignment_type || DASH;
  const status = line?.status ? titleCase(line.status) : '';
  const inactive = Boolean(line?.status) && String(line?.status).toLowerCase() !== 'active';
  const facts: string[] = [];
  if (line?.assignment_type && line.assignment_type !== name) facts.push(line.assignment_type);
  if (line?.cycle) facts.push(`Cycle ${line.cycle}`);
  if (status) facts.push(status);
  const range = line ? rangeText(line) : null;
  if (range) facts.push(range);
  return (
    <span
      className={cx('block max-w-[180px] truncate', name === DASH ? 'text-fg-subtle' : inactive ? 'text-fg-muted' : 'text-fg')}
      title={facts.length ? [name, ...facts].join(' · ') : name === DASH ? undefined : name}
    >
      {name}
    </span>
  );
}

// ---- specialists --------------------------------------------------------------------------------

type SpecialistLike = Pick<LaeProdSpecialist, 'name' | 'worked'> & {
  id?: string | number | null;
  assigned?: boolean | null;
};

function specialistFact(p: SpecialistLike): string {
  const name = p.name || 'Unknown';
  const facts = [p.worked ? 'worked' : 'no activity'];
  if (p.assigned === false) facts.push('not on team');
  return `${name} (${facts.join(', ')})`;
}

function specialistTitle(people: readonly SpecialistLike[]): string | undefined {
  return people.length ? people.map(specialistFact).join(', ') : undefined;
}

/** Specialists on one line, names inline in server order (worked first). */
export function SpecialistsLine({ line }: { line: { specialists?: readonly SpecialistLike[] | null } | null | undefined }) {
  const people = Array.isArray(line?.specialists) ? line.specialists : [];
  if (!people.length) return <span className="text-fg-subtle">{DASH}</span>;
  return (
    <span className="block max-w-[220px] truncate" title={specialistTitle(people)}>
      {people.map((p, i) => (
        <span key={p.id ?? `${p.name}-${i}`} className={p.worked ? 'text-fg' : 'text-fg-muted'}>
          {i > 0 ? <span className="text-fg-subtle">, </span> : null}
          {p.worked ? (
            <span aria-hidden="true" className="mr-1 inline-block size-1.5 rounded-full bg-success align-middle" />
          ) : null}
          {p.name || 'Unknown'}
        </span>
      ))}
    </span>
  );
}

/** Subtotal line: specialists who worked over everyone listed on the row's lines, e.g. `2 / 5`. */
export function SpecialistsTotal({ row }: { row: StackedRow }) {
  const { worked, total, people } = specialistTotals(row);
  return (
    <span className="tabular-nums" title={specialistTitle(people)}>
      <span className={isZeroish(worked) ? 'text-fg-subtle' : undefined}>{fmtNum(worked)}</span>
      <span className="font-normal text-fg-subtle"> / </span>
      <span>{fmtNum(total)}</span>
    </span>
  );
}
